import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Camera, X, RefreshCw, Check, AlertCircle, Sparkles, Loader2, SwitchCamera } from 'lucide-react';
import { convertToWebP, formatBytes } from '../../utils/imageUtils';

const CameraModal = ({ isOpen, onClose, onPhotoCaptured }) => {
    const videoRef = useRef(null);
    const canvasRef = useRef(null);
    const streamRef = useRef(null);

    const [devices, setDevices] = useState([]);
    const [selectedDeviceId, setSelectedDeviceId] = useState('');
    const [facingMode, setFacingMode] = useState('environment'); // 'environment' (back) or 'user' (front)
    const [cameraLoading, setCameraLoading] = useState(true);
    const [cameraError, setCameraError] = useState(null);
    
    // Captured photo state
    const [capturedData, setCapturedData] = useState(null); // { file, previewUrl, originalSize, webpSize, width, height }
    const [processing, setProcessing] = useState(false);
    const [flashEffect, setFlashEffect] = useState(false);

    // Stop current stream helper
    const stopStream = useCallback(() => {
        if (streamRef.current) {
            streamRef.current.getTracks().forEach((track) => track.stop());
            streamRef.current = null;
        }
    }, []);

    // List video devices
    const getDevices = useCallback(async () => {
        try {
            if (!navigator.mediaDevices?.enumerateDevices) return;
            const allDevices = await navigator.mediaDevices.enumerateDevices();
            const videoInputs = allDevices.filter((d) => d.kind === 'videoinput');
            setDevices(videoInputs);
        } catch (e) {
            console.warn('Could not enumerate video devices:', e);
        }
    }, []);

    // Start video stream
    const startStream = useCallback(async () => {
        setCameraLoading(true);
        setCameraError(null);
        stopStream();

        try {
            if (!navigator.mediaDevices?.getUserMedia) {
                throw new Error('Tu navegador no soporta captura de video en vivo.');
            }

            const constraints = {
                audio: false,
                video: selectedDeviceId
                    ? { deviceId: { exact: selectedDeviceId }, width: { ideal: 1920 }, height: { ideal: 1080 } }
                    : { facingMode: { ideal: facingMode }, width: { ideal: 1920 }, height: { ideal: 1080 } }
            };

            const stream = await navigator.mediaDevices.getUserMedia(constraints);
            streamRef.current = stream;

            if (videoRef.current) {
                videoRef.current.srcObject = stream;
                await videoRef.current.play();
            }

            await getDevices();
        } catch (err) {
            console.error('Camera stream error:', err);
            let msg = 'No se pudo acceder a la cámara.';
            if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
                msg = 'Permiso denegado para usar la cámara. Permite el acceso a la cámara en los ajustes de tu navegador.';
            } else if (err.name === 'NotFoundError' || err.name === 'DevicesNotFoundError') {
                msg = 'No se encontró ninguna cámara conectada en tu dispositivo.';
            } else if (err.name === 'NotReadableError') {
                msg = 'La cámara está siendo usada por otra aplicación.';
            }
            setCameraError(msg);
        } finally {
            setCameraLoading(false);
        }
    }, [facingMode, selectedDeviceId, stopStream, getDevices]);

    useEffect(() => {
        if (isOpen && !capturedData) {
            startStream();
        }
        return () => {
            stopStream();
        };
    }, [isOpen, capturedData, startStream, stopStream]);

    // Flip between back and front camera
    const toggleFacingMode = () => {
        if (devices.length > 1) {
            // Pick next device if available
            const currentIndex = devices.findIndex(d => d.deviceId === selectedDeviceId);
            const nextDevice = devices[(currentIndex + 1) % devices.length];
            setSelectedDeviceId(nextDevice.deviceId);
        } else {
            setFacingMode((prev) => (prev === 'environment' ? 'user' : 'environment'));
        }
    };

    // Take snapshot and immediately convert to WebP
    const capturePhoto = async () => {
        const video = videoRef.current;
        if (!video) return;

        try {
            setProcessing(true);
            setFlashEffect(true);
            setTimeout(() => setFlashEffect(false), 200);

            const width = video.videoWidth || 1280;
            const height = video.videoHeight || 720;

            const canvas = canvasRef.current || document.createElement('canvas');
            canvas.width = width;
            canvas.height = height;

            const ctx = canvas.getContext('2d');
            ctx.drawImage(video, 0, 0, width, height);

            // Convert canvas to blob
            const blob = await new Promise((resolve, reject) => {
                canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('Canvas error'))), 'image/jpeg', 0.95);
            });

            // Convert automatically to WebP format
            const webpResult = await convertToWebP(blob, {
                maxWidth: 1600,
                maxHeight: 1600,
                quality: 0.85,
                fallbackName: 'foto_accesorio.webp'
            });

            // Stop the live camera while reviewing photo
            stopStream();
            setCapturedData(webpResult);
        } catch (err) {
            console.error('Error processing photo to WebP:', err);
            alert('Error al capturar la foto: ' + err.message);
        } finally {
            setProcessing(false);
        }
    };

    // Discard preview and resume live stream
    const handleRetake = () => {
        if (capturedData?.previewUrl) {
            URL.revokeObjectURL(capturedData.previewUrl);
        }
        setCapturedData(null);
    };

    // Accept photo and pass to parent
    const handleConfirm = () => {
        if (!capturedData) return;
        onPhotoCaptured(capturedData);
        handleClose();
    };

    // Close and cleanup
    const handleClose = () => {
        stopStream();
        if (capturedData?.previewUrl) {
            URL.revokeObjectURL(capturedData.previewUrl);
        }
        setCapturedData(null);
        setCameraError(null);
        onClose();
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-fadeIn">
            <div className="relative w-full max-w-2xl bg-neutral-900 rounded-3xl overflow-hidden shadow-2xl border border-white/10 flex flex-col max-h-[92vh]">
                {/* Header */}
                <div className="px-6 py-4 flex items-center justify-between border-b border-white/10 bg-neutral-950/60">
                    <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-xl bg-red-600/20 text-red-500 flex items-center justify-center">
                            <Camera size={20} />
                        </div>
                        <div>
                            <h3 className="text-white font-black text-sm uppercase tracking-wider">
                                {capturedData ? 'Revisar Foto (WebP)' : 'Tirar Foto del Accesorio'}
                            </h3>
                            <p className="text-gray-400 text-xs">
                                {capturedData
                                    ? 'Conversión automática a WebP completada'
                                    : 'Apunta el accesorio y presiona el disparador'}
                            </p>
                        </div>
                    </div>

                    <button
                        type="button"
                        onClick={handleClose}
                        className="w-9 h-9 rounded-full bg-white/5 hover:bg-white/15 text-gray-300 hover:text-white flex items-center justify-center transition-colors"
                    >
                        <X size={18} />
                    </button>
                </div>

                {/* Body / Viewfinder */}
                <div className="relative flex-1 bg-black flex items-center justify-center min-h-[380px] max-h-[60vh] overflow-hidden">
                    {/* Flash animation */}
                    {flashEffect && (
                        <div className="absolute inset-0 bg-white z-50 animate-ping opacity-90 pointer-events-none" />
                    )}

                    {/* Hidden canvas for drawing snapshot */}
                    <canvas ref={canvasRef} className="hidden" />

                    {/* Camera Loading */}
                    {cameraLoading && !capturedData && (
                        <div className="flex flex-col items-center gap-3 text-white/60">
                            <Loader2 className="animate-spin text-red-500" size={32} />
                            <span className="text-xs uppercase tracking-widest font-semibold">Iniciando cámara...</span>
                        </div>
                    )}

                    {/* Camera Error */}
                    {cameraError && !capturedData && (
                        <div className="p-8 text-center max-w-md">
                            <div className="w-12 h-12 bg-red-500/10 text-red-500 rounded-2xl flex items-center justify-center mx-auto mb-3">
                                <AlertCircle size={24} />
                            </div>
                            <h4 className="text-white font-bold text-sm mb-1 uppercase tracking-wider">Cámara no disponible</h4>
                            <p className="text-gray-400 text-xs mb-4 leading-relaxed">{cameraError}</p>
                            <button
                                type="button"
                                onClick={startStream}
                                className="px-5 py-2.5 bg-white/10 hover:bg-white/20 text-white rounded-xl text-xs font-bold uppercase tracking-wider transition-colors inline-flex items-center gap-2"
                            >
                                <RefreshCw size={14} /> Reintentar
                            </button>
                        </div>
                    )}

                    {/* Live Video Viewfinder */}
                    {!capturedData && !cameraError && (
                        <div className="relative w-full h-full flex items-center justify-center">
                            <video
                                ref={videoRef}
                                playsInline
                                autoPlay
                                muted
                                className="w-full h-full object-contain"
                            />

                            {/* Viewfinder Overlay Guides */}
                            <div className="absolute inset-6 pointer-events-none border border-white/20 rounded-2xl">
                                <div className="absolute top-0 left-0 w-8 h-8 border-t-2 border-l-2 border-red-500 rounded-tl-lg" />
                                <div className="absolute top-0 right-0 w-8 h-8 border-t-2 border-r-2 border-red-500 rounded-tr-lg" />
                                <div className="absolute bottom-0 left-0 w-8 h-8 border-b-2 border-l-2 border-red-500 rounded-bl-lg" />
                                <div className="absolute bottom-0 right-0 w-8 h-8 border-b-2 border-r-2 border-red-500 rounded-br-lg" />

                                <div className="absolute top-3 left-3 bg-black/60 backdrop-blur-md px-2.5 py-1 rounded-full text-[10px] font-bold text-emerald-400 border border-emerald-500/30 flex items-center gap-1.5">
                                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                                    EN VIVO · AUTO WEBP
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Captured Photo Preview */}
                    {capturedData && (
                        <div className="relative w-full h-full flex items-center justify-center bg-black/95 p-4">
                            <img
                                src={capturedData.previewUrl}
                                alt="Captured accessory preview"
                                className="max-h-[50vh] max-w-full object-contain rounded-xl shadow-lg border border-white/10"
                            />

                            {/* Badge */}
                            <div className="absolute top-6 left-6 bg-emerald-500 text-black px-3 py-1 rounded-full text-[11px] font-black uppercase tracking-wider flex items-center gap-1.5 shadow-lg">
                                <Sparkles size={14} />
                                Formato WebP Generado
                            </div>

                            <div className="absolute bottom-6 right-6 bg-black/80 backdrop-blur-md text-white px-3 py-1.5 rounded-xl text-xs font-mono border border-white/10">
                                {capturedData.width} × {capturedData.height} · {formatBytes(capturedData.webpSize)}
                            </div>
                        </div>
                    )}
                </div>

                {/* Footer Controls */}
                <div className="p-6 bg-neutral-950/80 border-t border-white/10 flex items-center justify-between">
                    {!capturedData ? (
                        <>
                            {/* Switch Camera */}
                            <button
                                type="button"
                                onClick={toggleFacingMode}
                                title="Cambiar cámara"
                                className="px-4 py-2.5 bg-white/10 hover:bg-white/20 text-white rounded-xl text-xs font-bold uppercase tracking-wider transition-colors flex items-center gap-2"
                            >
                                <SwitchCamera size={16} />
                                <span className="hidden sm:inline">Girar</span>
                            </button>

                            {/* Shutter Button */}
                            <button
                                type="button"
                                disabled={cameraLoading || !!cameraError || processing}
                                onClick={capturePhoto}
                                className="relative group p-1.5 rounded-full border-4 border-white/30 hover:border-white transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                                <div className="w-16 h-16 rounded-full bg-red-600 hover:bg-red-500 transition-colors flex items-center justify-center text-white shadow-lg active:scale-90 transition-transform">
                                    {processing ? <Loader2 className="animate-spin" size={24} /> : <Camera size={26} />}
                                </div>
                            </button>

                            {/* Cancel */}
                            <button
                                type="button"
                                onClick={handleClose}
                                className="px-4 py-2.5 text-gray-400 hover:text-white text-xs font-bold uppercase tracking-wider transition-colors"
                            >
                                Cancelar
                            </button>
                        </>
                    ) : (
                        <div className="w-full flex items-center justify-between gap-4">
                            <button
                                type="button"
                                onClick={handleRetake}
                                className="px-5 py-3 bg-white/10 hover:bg-white/20 text-white rounded-xl text-xs font-bold uppercase tracking-wider transition-colors flex items-center gap-2"
                            >
                                <RefreshCw size={16} />
                                Tomar Otra
                            </button>

                            <button
                                type="button"
                                onClick={handleConfirm}
                                className="px-7 py-3 bg-emerald-500 hover:bg-emerald-400 text-black rounded-xl text-xs font-black uppercase tracking-wider transition-all flex items-center gap-2 shadow-lg shadow-emerald-500/20 active:scale-95"
                            >
                                <Check size={18} />
                                Usar Foto WebP
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default CameraModal;
